<template>
    <v-container fluid class="main-container d-flex flex-column pa-4">
        <v-card class="d-flex flex-column flex-grow-1">
            <v-card-title class="pa-4 d-flex align-center justify-center">

                <RouterLink class="mr-4 d-flex" v-slot="{ navigate, isActive }" to="/Customers" custom>
                    <v-icon-btn :active="isActive" color="secondary" variant="tonal" :icon="icons.mdiArrowLeft" tag="a"
                        density="default" @click="navigate" />
                </RouterLink>

                <v-select class="d-flex" :label="$t('field.customer')" v-model="customerSelector" base-color="primary"
                    @update:model-value="onCustomerChange" variant="outlined" :items="customers" item-title="name"
                    item-value="reference" style="max-width: 300px" />
                <v-spacer />

                <v-btn v-if="showAddButton" class="mr-2" color="secondary" density="default" :prepend-icon="icons.mdiPlus"
                    :disabled="!canAdd" @click="onAdd">{{ $t('action.add') }}</v-btn>
                <v-btn v-if="showSaveUndo" class="mr-2" color="error" density="default" :prepend-icon="icons.mdiRestore"
                    :disabled="!activeTabHasPendingChanges" @click="onUndoAll">{{ $t('action.undoAll') }}</v-btn>
                <v-btn v-if="showSaveUndo" color="secondary" density="default" :prepend-icon="icons.mdiContentSaveOutline"
                    :disabled="!activeTabHasPendingChanges" @click="onSave">{{ $t('action.save') }}</v-btn>
            </v-card-title>

            <v-container fluid class="tab-grid pa-0">
                <v-tabs v-model="tabComponent" color="secondary" grow :disabled="activeTabHasPendingChanges || activeTabHasPendingChanges">
                    <v-tab value="optionTab" :prepend-icon="icons.mdiApps">{{ $t('tab.applicationAccess') }}</v-tab>
                    <v-tab value="attributeTab" :prepend-icon="icons.mdiTune">{{ $t('tab.fieldsCustomization')}}</v-tab>
                    <v-tab value="ressourceTab" :prepend-icon="icons.mdiWeb">{{ $t('tab.translations') }}</v-tab>
                    <v-tab value="applicationDomainTab" :prepend-icon="icons.mdiDomain">{{ $t('tab.applicationDomain')}}</v-tab>
                    <v-tab value="alertTab" :prepend-icon="icons.mdiEmailAlertOutline">{{ $t('tab.mailAlert') }}</v-tab>
                    <v-tab value ="logAppointmentTab" :prepend-icon="icons.mdiClipboardTextClock">{{ $t('tab.logAppointment') }}</v-tab>
                </v-tabs>

                <v-tabs-window style="min-height: 0" v-model="tabComponent">
                    <v-tabs-window-item class="fill-height" value="optionTab" eager>
                        <OptionsTab ref="optionTabRef" :customer-reference="customerReference"
                            @pending-changes="tabPending.optionTab = $event" />
                    </v-tabs-window-item>

                    <v-tabs-window-item class="fill-height" value="attributeTab">
                        <AttributesTab ref="attributeTabRef" :customer-reference="customerReference"
                            @can-add-changed="attributeCanAdd = $event" />
                    </v-tabs-window-item>

                    <v-tabs-window-item class="fill-height" value="ressourceTab">
                        <TranslationsTab ref="translationTabRef" :customer-reference="customerReference"
                            @pending-changes="tabPending.ressourceTab = $event" />
                    </v-tabs-window-item>

                    <v-tabs-window-item class="fill-height" value="applicationDomainTab">
                        <ApplicationDomainTab ref="applicationDomainTabRef" :customer-reference="customerReference"
                            @pending-changes="tabPending.applicationDomainTab = $event" />
                    </v-tabs-window-item>

                    <v-tabs-window-item class="fill-height" value="alertTab">
                        <AlertsTab ref="alertTabRef" :customer-reference="customerReference"
                            @pending-changes="tabPending.alertTab = $event" />
                    </v-tabs-window-item>

                    <v-tabs-window-item class="fill-height" value="logAppointmentTab">
                        <LogAppointmentTab ref="logAppointmentTabRef" :customer-reference="customerReference"
                            @pending-changes="tabPending.logAppointmentTab = $event" />
                    </v-tabs-window-item>
                </v-tabs-window>
            </v-container>
        </v-card>
    </v-container>
</template>

<script setup>
    import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import OptionsTab from '@/views/tabs/ManageCustomer/OptionsTab.vue';
    import AttributesTab from '@/views/tabs/ManageCustomer/AttributesTab.vue';
    import TranslationsTab from '@/views/tabs/ManageCustomer/TranslationsTab.vue';
    import ApplicationDomainTab from '@/views/tabs/ManageCustomer/ApplicationDomainTab.vue';
    import AlertsTab from '@/views/tabs/ManageCustomer/AlertsTab.vue';
    import LogAppointmentTab from '@/views/tabs/ManageCustomer/LogAppointment.vue';
    import { toast } from 'vue3-toastify';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';
    import {
        mdiArrowLeft,
        mdiApps,
        mdiTune,
        mdiWeb,
        mdiDomain,
        mdiEmailAlertOutline,
        mdiPlus,
        mdiRestore,
        mdiContentSaveOutline,
        mdiClipboardTextClock
    } from '@mdi/js';

    const route = useRoute();
    const router = useRouter();
    const logStore = useLogStore();
    const userStore = useUserStore();

    const urlWebAPI = inject('urlWebAPI');

    const optionTabRef = ref(null);
    const attributeTabRef = ref(null);
    const translationTabRef = ref(null);
    const applicationDomainTabRef = ref(null);
    const alertTabRef = ref(null);
    const logAppointmentTabRef = ref(null);

    const TAB_REF_MAP = {
        optionTab: optionTabRef,
        attributeTab: attributeTabRef,
        ressourceTab: translationTabRef,
        applicationDomainTab: applicationDomainTabRef,
        alertTab: alertTabRef,
        logAppointmentTab: logAppointmentTabRef
    };

    const customerSelector = ref((route.params.customerReference != 'undefined') ? route.params.customerReference : null);
    const tabComponent = ref('optionTab');
    const customers = ref([]);

    const tabPending = reactive({
        optionTab: false,
        ressourceTab: false,
        applicationDomainTab: false,
        alertTab: false,
        logAppointmentTab: false,
    });
    const attributeCanAdd = ref(false);

    const icons = {
        mdiArrowLeft,
        mdiApps,
        mdiTune,
        mdiWeb,
        mdiDomain,
        mdiEmailAlertOutline,
        mdiPlus,
        mdiRestore,
        mdiContentSaveOutline,
        mdiClipboardTextClock
    };

    const customerReference = computed(() => (route.params.customerReference != 'undefined') ? route.params.customerReference : null);

    const activeTabRef = computed(() => TAB_REF_MAP[tabComponent.value]?.value);

    const activeTabHasPendingChanges = computed(() => !!tabPending[tabComponent.value]);

    const showAddButton = computed(() => tabComponent.value == 'attributeTab' || tabComponent.value == 'applicationDomainTab');

    const showSaveUndo = computed(() => tabComponent.value != 'attributeTab');

    const canAdd = computed(() => {
        if (tabComponent.value == 'attributeTab')
            return attributeCanAdd.value;

        return true;
    });

    watch(tabComponent, (value, oldValue) => {
        localStorage.setItem('activeManageTab', value);
    });

    //region created()
    getCustomers();
//endregion
    onMounted(() => {
        tabComponent.value = localStorage.getItem('activeManageTab') ?? tabComponent.value;
    });

    function getCustomers() {
        let data = {
            token: userStore.getUser().token,
        };

        $.getData(urlWebAPI + 'GetCustomers', data)
            .then((data) => {
                customers.value = data;
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetCustomers data.');
            });
    }

    function onCustomerChange(reference) {
        if (route.params.customerReference != reference) {
            router.push({ path: '/Customers/' + reference });

            setTimeout(() => {
                window.location.reload();
            }, 100);
        }
    }

    function onAdd() {
        if (tabComponent.value == 'attributeTab')
            attributeTabRef.value.openAddDialog();

        else if (tabComponent.value == 'applicationDomainTab')
            applicationDomainTabRef.value.openAddDialog();
    }

    function onUndoAll() {
        activeTabRef.value?.undo();
    }

    function onSave() {
        activeTabRef.value?.save();
    }
</script>
